import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCalendar, FiSearch } from 'react-icons/fi';

// Animatie varianten
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Nieuwsberichten data
const newsItems = [
  {
    id: 1,
    title: 'Gasten Hospice in Hoorn kunnen zelf hun dossier bijhouden',
    excerpt: 'De Ipads die Hospice Dignitas in Hoorn recent van serviceclub Lions Hoorn De Doelen cadeau kreeg, hebben inmiddels een belangrijke functie gekregen in de zorg voor gasten.',
    content: 'De Ipads die Hospice Dignitas in Hoorn recent van serviceclub Lions Hoorn De Doelen cadeau kreeg, hebben inmiddels een belangrijke functie gekregen in de zorg voor gasten. Dankzij een speciale app kunnen gasten nu zelf hun dossier bijhouden en aangeven hoe ze zich voelen. Dit helpt het zorgpersoneel om de zorg nog beter af te stemmen op de behoeften van de gasten.',
    image: '/images/news-hospice.jpg',
    date: '2 mei 2023',
    category: 'Donatie',
    slug: 'hospice-ipads'
  },
  {
    id: 2,
    title: '€250.408 opbrengst voor Oekraïne',
    excerpt: 'Lions Clubs in Nederland hebben gezamenlijk een indrukwekkend bedrag opgehaald voor hulp aan Oekraïne.',
    content: 'Lions Clubs in Nederland hebben gezamenlijk een indrukwekkend bedrag van €250.408 opgehaald voor hulp aan Oekraïne. Dit geld wordt gebruikt voor directe noodhulp en voor de wederopbouw van het land. Lions Club Hoorn De Doelen heeft hier actief aan bijgedragen door verschillende acties te organiseren.',
    image: '/images/news-ukraine.jpg',
    date: '1 juli 2023',
    category: 'Internationaal',
    slug: 'opbrengst-oekraine'
  },
  {
    id: 3,
    title: 'Laatste nieuws actie "Warmte voor Oekraïne"',
    excerpt: 'De actie "Warmte voor Oekraïne" is in volle gang. Hier vindt u het laatste nieuws over deze belangrijke hulpactie.',
    content: 'De actie "Warmte voor Oekraïne" is in volle gang. Lions Clubs in heel Nederland zamelen geld en goederen in om de Oekraïense bevolking te helpen de winter door te komen. Er worden dekens, warme kleding en verwarmingsapparatuur ingezameld en naar Oekraïne getransporteerd. Ook Lions Club Hoorn De Doelen doet mee aan deze actie.',
    image: '/images/news-warmte.jpg',
    date: '17 januari 2023',
    category: 'Actie',
    slug: 'warmte-oekraine'
  },
  {
    id: 4,
    title: 'Succesvolle boekenmarkt levert €5.000 op voor goede doelen',
    excerpt: 'De jaarlijkse boekenmarkt van Lions Club Hoorn De Doelen was een groot succes en heeft €5.000 opgeleverd voor goede doelen.',
    content: 'De jaarlijkse boekenmarkt van Lions Club Hoorn De Doelen was een groot succes en heeft €5.000 opgeleverd voor goede doelen. Het geld gaat naar Hospice Dignitas in Hoorn en Make-A-Wish Nederland. Dankzij de vele bezoekers en de gulle donaties van boeken door inwoners van Hoorn en omgeving, was de boekenmarkt een groot succes.',
    image: '/images/news-boekenmarkt.jpg',
    date: '15 april 2023',
    category: 'Fundraising',
    slug: 'boekenmarkt-2023'
  },
  {
    id: 5,
    title: 'Lions Club Hoorn De Doelen viert 15-jarig bestaan',
    excerpt: 'Lions Club Hoorn De Doelen heeft haar 15-jarig bestaan gevierd met een feestelijke bijeenkomst voor leden en partners.',
    content: 'Lions Club Hoorn De Doelen heeft haar 15-jarig bestaan gevierd met een feestelijke bijeenkomst voor leden en partners. Tijdens de bijeenkomst werd teruggeblikt op 15 jaar service aan de gemeenschap en werden plannen gemaakt voor de toekomst. De club werd opgericht op 11 juni 2005 en heeft sindsdien talloze projecten en acties georganiseerd om geld in te zamelen voor goede doelen.',
    image: '/images/news-anniversary.jpg',
    date: '11 juni 2020',
    category: 'Club',
    slug: '15-jaar-lions-club'
  },
  {
    id: 6,
    title: 'Vredesposterwedstrijd 2023 van start',
    excerpt: 'De jaarlijkse Vredesposterwedstrijd voor kinderen is weer van start gegaan. Scholen in Hoorn en omgeving kunnen zich aanmelden.',
    content: 'De jaarlijkse Vredesposterwedstrijd voor kinderen is weer van start gegaan. Scholen in Hoorn en omgeving kunnen zich aanmelden om deel te nemen aan deze internationale wedstrijd. Kinderen worden aangemoedigd om na te denken over vrede en dit uit te drukken in een poster. De winnende poster uit Hoorn doet mee aan de landelijke en mogelijk internationale competitie.',
    image: '/images/news-vredesposter.jpg',
    date: '1 september 2023',
    category: 'Educatie',
    slug: 'vredesposter-2023'
  }
];

const News: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredNews, setFilteredNews] = useState(newsItems);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.trim() === '') {
      setFilteredNews(newsItems);
    } else {
      const filtered = newsItems.filter(
        item => 
          item.title.toLowerCase().includes(term.toLowerCase()) ||
          item.excerpt.toLowerCase().includes(term.toLowerCase()) ||
          item.category.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredNews(filtered);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-lions-dark text-white py-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/news-hero.jpg" 
            alt="Nieuws van Lions Club Hoorn De Doelen" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.h1 
              variants={fadeIn}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Laatste <span className="text-lions-yellow">Nieuws</span>
            </motion.h1>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Blijf op de hoogte van de laatste ontwikkelingen en activiteiten van Lions Club Hoorn De Doelen.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Nieuws Section */}
      <section className="section bg-white dark:bg-lions-dark">
        <div className="container-custom">
          {/* Zoekbalk */}
          <div className="mb-12">
            <div className="relative max-w-md mx-auto">
              <input
                type="text"
                placeholder="Zoek in nieuwsberichten..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full px-4 py-3 pl-12 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-lions-blue dark:focus:ring-lions-yellow"
              />
              <FiSearch className="absolute left-4 top-3.5 text-gray-500 dark:text-gray-400" size={20} />
            </div>
          </div>

          {/* Nieuwsberichten Grid */}
          {filteredNews.length > 0 ? (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredNews.map((item) => (
                <motion.div
                  key={item.id}
                  variants={fadeIn}
                  className="bg-lions-light dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
                >
                  <img 
                    src={item.image} 
                    alt={item.title} 
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center text-gray-500 dark:text-gray-400">
                        <FiCalendar className="mr-2" /> {item.date}
                      </div>
                      <span className="inline-block px-2 py-1 bg-lions-blue text-white text-xs rounded-full">
                        {item.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      {item.excerpt}
                    </p>
                    <Link to={`/nieuws/${item.slug}`} className="text-lions-blue dark:text-lions-yellow hover:underline flex items-center">
                      Lees meer <FiArrowRight className="ml-2" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold mb-2">Geen nieuwsberichten gevonden</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Probeer een andere zoekterm of bekijk alle nieuwsberichten.
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilteredNews(newsItems);
                }}
                className="btn-primary"
              >
                Alle nieuwsberichten
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Nieuwsbrief Section */}
      <section className="py-16 bg-lions-blue text-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              Blijf op de hoogte
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Schrijf je in voor onze nieuwsbrief en ontvang het laatste nieuws over onze activiteiten en projecten.
            </motion.p>
            <motion.div 
              variants={fadeIn}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <input
                type="email"
                placeholder="Uw e-mailadres"
                className="px-4 py-2 rounded text-gray-700 w-full sm:w-auto"
              />
              <button className="btn-secondary">
                Inschrijven
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default News; 